// supabase.ts — where the app talks to Supabase (identity + entitlement).
//
// Values come from a GITIGNORED `.env` at the repo root (Vite `VITE_*` vars).
// Without them the app runs UNGATED (no sign-in required) using the fallbacks
// below — same fail-soft posture as the old Thinkific config.
//
// .env keys: VITE_SUPABASE_URL, VITE_SUPABASE_ANON_KEY, VITE_PURCHASE_URL.
// In-app purchase (RevenueCat public SDK keys, native only): VITE_REVENUECAT_IOS_KEY,
// VITE_REVENUECAT_ANDROID_KEY, VITE_REVENUECAT_TEST_KEY (dev builds only fallback).
import { Capacitor } from '@capacitor/core';

const env = import.meta.env;

export const SUPABASE = {
  url: (env.VITE_SUPABASE_URL as string) || '',
  anonKey: (env.VITE_SUPABASE_ANON_KEY as string) || '',
};

// Where a free user goes to buy (the website checkout). Opens in the system
// browser; purchases happen on the web (no in-app purchase). Defaults to the
// live buy page so the trial-end / lock prompts always have a destination even
// if VITE_PURCHASE_URL isn't injected at build time; the env var can override.
export const PURCHASE_URL: string = (env.VITE_PURCHASE_URL as string) || 'https://www.renurture.org/buy';

// Purchasing is WEB ONLY. The native iOS + Android apps ship with NO in-app
// purchasing and NO purchase prompts at all — both Apple (App Store 3.1.1) and Google
// (Play Billing) require digital-goods purchases to go through their own billing, so
// we simply don't sell in the app. Native locked/trial screens stay informational
// (no buy button, no link, no price) and tell the user their membership is managed on
// the ReNurture website; a user who joins on the web just signs in to unlock here.
// The WEB build keeps the full purchase journey unchanged. Every purchase CTA must
// stay gated on this flag, so the single switch below is the whole branch.
export const SHOW_PURCHASE: boolean = !Capacitor.isNativePlatform();

// In-app purchase (RevenueCat). Native only, and only when a public SDK key is present
// for this platform; with no key SHOW_IAP is false and native behaves exactly as
// before (informational, purchase-free). SHOW_PURCHASE above still means "web Stripe".
const devTestKey = env.DEV ? ((env.VITE_REVENUECAT_TEST_KEY as string) || '') : '';
export const REVENUECAT_IOS_KEY: string = (env.VITE_REVENUECAT_IOS_KEY as string) || devTestKey;
export const REVENUECAT_ANDROID_KEY: string = (env.VITE_REVENUECAT_ANDROID_KEY as string) || devTestKey;
export const REVENUECAT_KEY: string = !Capacitor.isNativePlatform()
  ? ''
  : Capacitor.getPlatform() === 'ios' ? REVENUECAT_IOS_KEY : REVENUECAT_ANDROID_KEY;
export const SHOW_IAP: boolean = Capacitor.isNativePlatform() && !!REVENUECAT_KEY;

// The app's custom URL scheme Supabase redirects social logins back to.
// Must match capacitor appId + the native URL-scheme registration.
export const OAUTH_REDIRECT_NATIVE = 'org.renurture.app://auth/callback';

/** True when Supabase is configured enough to attempt sign-in. */
export const ACTIVATION_ENABLED = (): boolean => Boolean(SUPABASE.url && SUPABASE.anonKey);
